import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import * as LOCAL_STORAGE_KEYS from 'shared/constants/localStorageKeys';
import env from "../shared/constants/env";
import { getApiEndpoint } from "../utilities/apiEndpoint";
import hashedPassword from "../utilities/hashedPassword";

/**
 * Get the upgrade flag from localStorage
 * @returns {boolean} true if upgrade flag is set to true, false otherwise
 */
const getUpgradeFlag = () => {
  try {
    const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));
    return user?.upgrade === true;
  } catch (error) {
    console.error("Error parsing user data from localStorage:", error);
    return false;
  }
};

/**
 * Get the authentication token from localStorage
 * @returns {string|null} token if available, null otherwise
 */
const getAuthToken = () => {
  try {
    const user = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.USER));
    return user?.token || null;
  } catch (error) {
    console.error("Error parsing user data from localStorage:", error);
    return null;
  }
};

// Helper function to check if response transformation is needed
const shouldTransformResponse = () => {
  try {
    return !getUpgradeFlag(); // Transform only if NOT upgraded (v1 format)
  } catch (error) {
    console.error("Error checking upgrade flag:", error);
    return true; // Default to transform (v1 format) if there's an error
  }
};

// Helper function to get the appropriate status parameter based on upgrade flag
const getStatusParam = (defaultStatus = 'ACTIVE') => {
  try {
    return getUpgradeFlag() ? defaultStatus : 'A'; // Use 'A' for v1, original status for v2
  } catch (error) {
    console.error("Error checking upgrade flag:", error);
    return 'A'; // Default to 'A' (v1 format) if there's an error
  }
};

// Helper function to determine API endpoint based on application source for v1 users
const getEndpointForApplication = (appId, requestBody) => {
  // For v2 users (upgraded), always use the conditional endpoint logic
  console.log(requestBody, "request body")
  if (getUpgradeFlag()) {
    return null; // Let the existing conditional logic handle it
  }

  // For v1 users, check if we have application source information in the request
  // This assumes the frontend will pass the __apiSource information
  if (requestBody && requestBody.__apiSource) {
    return requestBody.__apiSource === 'v1' ? env.API_ENDPOINT : env.API_ENDPOINT_V2;
  }

  // If no source information available, try to get it from localStorage cache
  try {
    const appSourceMap = JSON.parse(localStorage.getItem('userAppsSourceMap') || '{}');
    if (appSourceMap[appId]) {
      return appSourceMap[appId] === 'v1' ? env.API_ENDPOINT : env.API_ENDPOINT_V2;
    }
  } catch (error) {
    console.error("Error checking cached app source:", error);
  }

  return null; // Fall back to existing conditional logic
};

// Create a dynamic base query that uses conditional endpoint
const dynamicBaseQuery = fetchBaseQuery({
  baseUrl: "",
  prepareHeaders: (headers) => {
    return headers;
  },
});

// Helper function to check for version parameter in current URL
const getVersionFromURL = () => {
  try {
    if (typeof window !== 'undefined' && window.location) {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('version');
    }
  } catch (error) {
    console.error('Error reading URL parameters:', error);
  }
  return null;
};

const baseQueryWithConditionalEndpoint = async (args, api, extraOptions) => {
  // Special handling for endpoints that always use specific versions
  let baseUrl;


  // Check if this is an application-specific endpoint and we have source information
  const appSpecificEndpoints = [
    'app/get-unsigned-contract', 'app/get-signed-contract', 'app/get-offer', 'app/update-offer',
    'app/update-details-cust', 'app/documents-settings', 'app/document', 'app/upload-document', 'app/customer-contract-decision',
    'app/appl_rating'
  ];

  const isAppSpecificEndpoint = appSpecificEndpoints.some(endpoint => args.url.startsWith(endpoint));

  if (isAppSpecificEndpoint && !getUpgradeFlag()) {
    // For v1 users on app-specific endpoints, try to determine source
    let appId = null;

    // Extract appId from URL or body
    if (args.url.includes('appId=')) {
      appId = args.url.split('appId=')[1].split('&')[0];
    } else if (args.url.includes('applId=')) {
      appId = args.url.split('applId=')[1].split('&')[0];
    } else if (args.body && (args.body.appId || args.body.applId)) {
      appId = args.body.appId || args.body.applId;
    }

    const sourceBasedEndpoint = getEndpointForApplication(appId, args.body);
    if (sourceBasedEndpoint) {
      baseUrl = sourceBasedEndpoint;
    }
  }

  // If not determined by application source, use existing logic
  if (!baseUrl) {
    if (args.url === 'auth/register' || args.url === 'app/insert-app-personal' || args.url === 'app/insert-app-murabaha' || args.url === 'app/insert-app-ijarah' || args.url === 'app/insert-app-restructure' || args.url === 'app/insert-app-topup' || args.url === 'app/insert-app-buyout' || args.url === 'app/settlement-app' || args.url === 'app/adhoc-app' || args.url === 'app/partialsettlement-app' || args.url === 'app/settlement-quote' || args.url === 'app/get-noc-letter' || (args.url === 'app/update-details-application' && args.method === 'POST')) {
      // Always use v2 endpoint for register, murabaha, ijarah, Other Services
      // (restructure / topup / early settlement / cancelation / clearance letter)
      // and Vehicle Services (ad-hoc charges)
      baseUrl = env.API_ENDPOINT_V2;
    }
    //commenting out pre-app-status v1 v2 logic
    // else if (args.url.startsWith('app/pre-app-status')) {
    // Always use v2 endpoint for getPreAppStatus
    // baseUrl = env.API_ENDPOINT_V2;
    // }
    else if (args.url.startsWith('lookup/')) {
      // Always use v2 endpoint for all lookup APIs (default behavior)
      baseUrl = env.API_ENDPOINT_V2;
    } else if (args.url.startsWith('vendors')) {
      // V2-only — dynamic vendor catalog (no v1 equivalent).
      baseUrl = env.API_ENDPOINT_V2;
    } else if (args.url === 'app/get-marital-status' || args.url === 'app/get-eligibility' || args.url === 'app/get-address' || args.url === 'app/get-general-info' || args.url === 'app/update-marital-status' || args.url === 'app/update-eligibility' || args.url === 'app/update-address' || args.url === 'app/update-general-info') {
      // Check for version parameter in URL for Settings endpoints
      const urlVersion = getVersionFromURL();
      console.log(`Settings endpoint ${args.url} - URL version:`, urlVersion);
      if (urlVersion) {
        // Use explicit version from URL parameter
        baseUrl = urlVersion === '1' ? env.API_ENDPOINT : env.API_ENDPOINT_V2;
      } else {
        // Default to v2 endpoint for these specific app endpoints when no URL version
        baseUrl = getUpgradeFlag() ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;
      }
    } else if (args.url === 'app/get-advert-consent' || args.url === 'app/reset-password' || args.url === 'auth/verify-reset-password' || args.url === 'app/get-instlmnt-dtl' || (args.url === 'app/update-details-application' && args.method === 'PUT') || (args.url === 'app/insert-app') || args.url === 'app/update-advert-consent' || args.url === 'app/update-user-email' || args.url === 'app/update-privacy-consent' || args.url === 'app/check-eligibility' || args.url === 'app/get-salary' || args.url === 'app/update-salary') {
      // Use v1 endpoint for these specific endpoints
      baseUrl = env.API_ENDPOINT;
    } else {
      // Get the appropriate endpoint based on the upgrade flag for other endpoints
      baseUrl = getApiEndpoint();
    }
  }

  // Modify the args to include the baseUrl
  const argsWithBaseUrl = {
    ...args,
    url: `${baseUrl}${args.url}`,
  };

  return dynamicBaseQuery(argsWithBaseUrl, api, extraOptions);
};

export const api = createApi({
  baseQuery: baseQueryWithConditionalEndpoint,
  endpoints: (builder) => ({
    signIn: builder.mutation({
      query: (credentials) => ({
        url: "auth/login",
        method: "POST",
        body: {
          iqamaId: credentials.iqamaId,
          password: hashedPassword(credentials.password),
          deviceId: credentials.deviceId,
        },
      }),
    }),
    verify: builder.mutation({
      query: (credentials) => ({
        url: "auth/verify",
        method: "POST",
        body: { ...credentials, otp: hashedPassword(credentials.otp) },
      }),
    }),
    forgotPassword: builder.mutation({
      query: (body) => ({
        url: "auth/forgot-password",
        method: "POST",
        body,
      }),
    }),
    forgotPasswordVerification: builder.mutation({
      query: (body) => ({
        url: "auth/forgot-password-verify",
        method: "POST",
        body: {
          ...body, otp: hashedPassword(body.otp)
        },
      }),
    }),
    resetPassword: builder.mutation({
      query: (body) => ({
        url: "auth/verify-reset-password",
        method: "POST",
        body: {
          ...body, otp: hashedPassword(body.otp)
        },
      }),
    }),
    validateId: builder.mutation({
      query: (body) => ({
        url: "auth/validate-id",
        method: "POST",
        body: {
          ...body,
          source: 'web',
        },
      }),
    }),
    register: builder.mutation({
      query: (body) => ({
        url: "auth/register",
        method: "POST",
        body,
      }),
    }),
    getStatus: builder.mutation({
      query: (body) => ({
        url: "auth/get-status",
        method: "POST",
        body,
      }),
    }),
    CheckPassword: builder.mutation({
      query: (body) => ({
        url: "app/reset-password",
        method: "POST",
        body,
      }),
    }),
    validatePhone: builder.mutation({
      query: (body) => ({
        url: "auth/validate-tahqoq-info",
        method: "POST",
        body,
      }),
    }),
    logout: builder.mutation({
      query: (body) => ({
        url: "auth/logout",
        method: "POST",
        body,
      }),
    }),
    getPersonalInformation: builder.mutation({
      query: (body) => ({
        url: "app/personal-info",
        method: "POST",
        body,
      }),
    }),
    getApplication: builder.query({
      query: (appID) => ({ url: `app/user-app?appId=${appID}` }),
      transformResponse: (response) => response?.payload?.rows || [],
      transformErrorResponse: (response) => ({
        error: "FAILED_TO_LOAD_APP",
      }),
      keepUnusedDataFor: 0, //for not caching data
    }),
    createNewApp: builder.mutation({
      query: (body) => ({
        url: "app/insert-app",
        method: "POST",
        body,
      }),
    }),
    createNewTawaruqApp: builder.mutation({
      query: (body) => ({
        url: "app/insert-app-personal",
        method: "POST",
        body,
      }),
    }),
    createNewMurabahaApp: builder.mutation({
      query: (body) => ({
        url: "app/insert-app-murabaha",
        method: "POST",
        body,
      }),
    }),
    createNewIjarahApp: builder.mutation({
      query: (body) => ({
        url: "app/insert-app-ijarah",
        method: "POST",
        body,
      }),
    }),
    // Finance Restructure submit (forced v2 — see baseQuery above).
    insertAppRestructure: builder.mutation({
      query: (body) => ({
        url: "app/insert-app-restructure",
        method: "POST",
        body,
      }),
    }),
    // Topup submit (forced v2 — see baseQuery above).
    insertAppTopup: builder.mutation({
      query: (body) => ({
        url: "app/insert-app-topup",
        method: "POST",
        body,
      }),
    }),
    // Buyout submit (forced v2 — see baseQuery above). Same payload shape as
    // restructure; product refs and loan amount are derived server-side.
    insertAppBuyout: builder.mutation({
      query: (body) => ({
        url: "app/insert-app-buyout",
        method: "POST",
        body,
      }),
    }),
    // Unified Early Settlement and Finance Cancelation submit.
    insertAppSettlement: builder.mutation({
      query: (body) => ({
        url: "app/settlement-app",
        method: "POST",
        body,
      }),
    }),
    // Ad-hoc charges submit (forced v2 — see baseQuery above). Backs every
    // Vehicle Service; only `details.chargeType` varies between them.
    // Responds with the raw Temenos body (header.status === "success").
    insertAppAdhocCharge: builder.mutation({
      query: (body) => ({
        url: "app/adhoc-app",
        method: "POST",
        body,
      }),
    }),
    // Partial settlement submit (forced v2). details.repaymentType picks the
    // Temenos route server-side. Responds with the raw Temenos body.
    insertAppPartialSettlement: builder.mutation({
      query: (body) => ({
        url: "app/partialsettlement-app",
        method: "POST",
        body,
      }),
    }),
    // Early-settlement payoff quote — read-only. Same { appId, id } envelope as
    // the NOC letter. Responds with { data: { payoffAmount } }.
    getSettlementQuote: builder.mutation({
      query: (body) => ({
        url: "app/settlement-quote",
        method: "POST",
        body,
      }),
    }),
    // Clearance (NOC) letter — forced v2. NOTE: a different envelope from the
    // other Other Services calls: { appId, id } only, where `id` is IDB_USR_ID
    // as a number. Responds either with { status: "PENDING" } (letter not
    // generated yet) or { payload: { content: <base64 pdf> } }.
    getNocLetter: builder.mutation({
      query: (body) => ({
        url: "app/get-noc-letter",
        method: "POST",
        body,
      }),
    }),
    getUserApps: builder.query({
      queryFn: async (idbUserId, { signal, getState }, extraOptions) => {
        // const upgradeFlag = getUpgradeFlag();

        // if (upgradeFlag) {
        //   // For v2 users (upgraded), use regular query with baseQuery
        //   const baseQuery = fetchBaseQuery({
        //     baseUrl: getApiEndpoint(),
        //     prepareHeaders: (headers, { getState }) => {
        //       const token = getAuthToken();
        //       if (token) {
        //         headers.set('authorization', `Bearer ${token}`);
        //       }
        //       return headers;
        //     },
        //   });

        //   const result = await baseQuery(
        //     { url: `app/user-apps?idbUserId=${idbUserId}` },
        //     { signal, getState },
        //     extraOptions
        //   );

        //   if (result.error) {
        //     return { error: { error: "FAILED_TO_LOAD_APPS" } };
        //   }

        //   return { data: result.data?.payload?.rows || [] };
        // } else {
        // For v1 users (not upgraded), call both v1 and v2 endpoints and combine results
        const baseQueryV1 = fetchBaseQuery({
          baseUrl: env.API_ENDPOINT,
          prepareHeaders: (headers, { getState }) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const baseQueryV2 = fetchBaseQuery({
          baseUrl: env.API_ENDPOINT_V2,
          prepareHeaders: (headers, { getState }) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        try {
          const [v1Result, v2Result] = await Promise.allSettled([
            baseQueryV1(
              { url: `app/user-apps?idbUserId=${idbUserId}` },
              { signal, getState },
              extraOptions
            ),
            baseQueryV2(
              { url: `app/user-apps?idbUserId=${idbUserId}` },
              { signal, getState },
              extraOptions
            )
          ]);

          let combinedRows = [];

          // Process v1 response
          if (v1Result.status === 'fulfilled' && !v1Result.value.error) {
            const v1Data = v1Result.value.data;
            const v1Rows = v1Data?.payload?.rows || [];
            // Tag v1 applications with source
            const taggedV1Rows = v1Rows.map(app => ({
              ...app,
              __apiSource: 'v1' // Internal flag to track source
            }));
            combinedRows = [...combinedRows, ...taggedV1Rows];
          }

          // Process v2 response
          if (v2Result.status === 'fulfilled' && !v2Result.value.error) {
            const v2Data = v2Result.value.data;
            const v2Rows = v2Data?.payload?.rows || [];
            // Tag v2 applications with source
            const taggedV2Rows = v2Rows.map(app => ({
              ...app,
              __apiSource: 'v2' // Internal flag to track source
            }));
            combinedRows = [...combinedRows, ...taggedV2Rows];
          }

          // Remove duplicates based on application ID if needed
          const uniqueRows = combinedRows.filter((app, index, self) =>
            index === self.findIndex((t) => t.APPLID === app.APPLID)
          );
          console.log("unique rows", uniqueRows);
          return { data: uniqueRows };
        } catch (error) {
          return { error: { error: "FAILED_TO_LOAD_APPS" } };
        }

      },
    }),
    updateAppDetails: builder.mutation({
      query: (body) => ({
        url: "app/update-details-application",
        method: "PUT",
        body,
      }),
    }),
    updateAppDetailsV2: builder.mutation({
      query: (body) => ({
        url: "app/update-details-application",
        method: "POST",
        body,
      }),
    }),
    updateCustomerDetails: builder.mutation({
      query: (body) => ({
        url: "app/update-details-cust",
        method: "PUT",
        body,
      }),
    }),
    getApplicationOffer: builder.query({
      queryFn: async (params, api, extraOptions) => {
        // Extract appId and apiVersion from params
        const appId = typeof params === 'object' ? params.appId : params;
        const apiVersion = typeof params === 'object' ? params.apiVersion : '1';

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `app/get-offer?appId=${appId}` },
          api,
          extraOptions
        );

        if (result.error) {
          return { error: { error: "FAILED_TO_LOAD_OFFER" } };
        }

        const response = result.data;

        //transform response
        return { data: response?.payload?.length > 0 ? response?.payload[0] : null };

      },
      keepUnusedDataFor: 0,
    }),
    updateAppOffer: builder.mutation({
      queryFn: async (body, api, extraOptions) => {
        // Extract apiVersion from body
        const apiVersion = body.apiVersion || '1';

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        // Remove apiVersion from body before sending
        const { apiVersion: _, ...requestBody } = body;

        const result = await versionedBaseQuery(
          {
            url: "app/update-offer",
            method: "PUT",
            body: requestBody,
          },
          api,
          extraOptions
        );

        if (result.error) return result;

        return { data: result.data };
      },
    }),
    getMaritalStatus: builder.mutation({
      query: (body) => ({
        url: "app/get-marital-status",
        method: "POST",
        body,
      }),
    }),
    updateMaritalStatus: builder.mutation({
      query: (body) => ({
        url: "app/update-marital-status",
        method: "POST",
        body,
      }),
    }),
    getEligibility: builder.mutation({
      query: (body) => ({
        url: "app/get-eligibility",
        method: "POST",
        body,
      }),
    }),
    updateEligibility: builder.mutation({
      query: (body) => ({
        url: "app/update-eligibility",
        method: "POST",
        body,
      }),
    }),
    getAddressDetail: builder.mutation({
      query: (body) => ({
        url: "app/get-address",
        method: "POST",
        body,
      }),
    }),
    updateAddressDetail: builder.mutation({
      query: (body) => ({
        url: "app/update-address",
        method: "POST",
        body: {
          ...body,
        },
      }),
    }),
    getpersonalInfo: builder.mutation({
      query: (body) => ({
        url: "app/get-general-info",
        method: "POST",
        body: {
          ...body,
        },
      }),
    }),
    updateUserEmail: builder.mutation({
      query: (body) => ({
        url: "app/update-user-email",
        method: "POST",
        body,
      }),
    }),
    getDocuments: builder.query({
      queryFn: async (body, api, extraOptions) => {
        // Determine API version from body or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (body && body.apiVersion) {
          // Explicit version provided in request
          apiVersion = body.apiVersion;
        } else {
          // Check upgrade flag to determine version
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            // Get token from localStorage and add to headers
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        // Make the request with just the endpoint path
        return versionedBaseQuery(
          { url: `app/documents-settings?id=${body.id}${body.applId ? `&applId=${body.applId}` : ""}` },
          api,
          extraOptions
        );
      },
      keepUnusedDataFor: 0,
    }),
    getPreAppStatus: builder.query({
      queryFn: async (body, api, extraOptions) => {
        // Determine API version from body or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (body && body.apiVersion) {
          // Explicit version provided in request
          apiVersion = body.apiVersion;
        } else {
          // Check upgrade flag to determine version
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        console.log('Pre-app-status API version:', apiVersion, 'BaseURL:', baseUrl);

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            // Get token from localStorage and add to headers
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        // Make the request with just the endpoint path
        return versionedBaseQuery(
          { url: `app/pre-app-status?id=${body.id}` },
          api,
          extraOptions
        );
      },
      keepUnusedDataFor: 0
    }),
    ibanVerification: builder.mutation({
      query: (body) => ({
        url: "app/iban_verification",
        method: "POST",
        body,
      }),
    }),
    updateGeneralInfo: builder.mutation({
      query: (body) => ({
        url: "app/update-general-info",
        method: "POST",
        body: {
          ...body,
        },
      }),
    }),
    deleteAppl: builder.mutation({
      query: (body) => ({
        url: `app/delete-app?appId=${body}`,
        method: "DELETE",
      }),
    }),
    getManageOffers: builder.mutation({
      query: (body) => ({
        url: "app/get-advert-consent",
        method: "POST",
        body,
      }),
    }),
    updateManageOffers: builder.mutation({
      query: (body) => ({
        url: "app/update-advert-consent",
        method: "POST",
        body,
      }),
    }),
    getInstlmntDashboard: builder.mutation({
      queryFn: async (body, api, extraOptions) => {
        const baseUrl = getUpgradeFlag() ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;
        const url = getUpgradeFlag() ? 'app/view-contract' : 'app/get-instlmnt-dtl';

        const versionedBaseQuery = fetchBaseQuery({
          baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) headers.set('authorization', `Bearer ${token}`);
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url, method: 'POST', body },
          api,
          extraOptions
        );

        if (result.error) return { error: result.error };
        return { data: result.data };
      },
    }),
    // Lookup endpoints
    getNationalities: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/nationalities?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        const response = result.data;
        if (params.apiVersion == '2') {  //if we have explicit version param to be 2, then only check upgrade flag
          if (!shouldTransformResponse()) {
            return { data: response }; // Return as-is for v2 (upgraded)
          }
        }

        const rows = response?.payload?.rows || [];
        const final =
        {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.NATIONALITYID,
                DESCRIPTION: row.NATIONALITY,
                DESCRIPTIONAR: row.NATIONALITY_AR,
                ...row
              }))
            }
          }
        };
        return final
      },
    }),
    getEmploymentSectors: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/empl_sector?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.SECTORID,
                DESCRIPTION: row.SECTOR,
                DESCRIPTIONAR: row.SECTOR_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getEducationLevels: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/education_level?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.QUALIFICATIONID,
                DESCRIPTION: row.QUALIFICATION,
                DESCRIPTIONAR: row.QUALIFICATION_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getResidentialStatus: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/residential_status?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.RESIDENTID,
                DESCRIPTION: row.RESIDENT_STATUS,
                DESCRIPTIONAR: row.RESIDENT_STATUS_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getEmploymentCategories: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/employment-categories?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.EMPCATGID,
                DESCRIPTION: row.EMPCATGNAME,
                DESCRIPTIONAR: row.EMPCATGNAME_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getRelatedParty: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/related-party?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.PARTYID,
                DESCRIPTION: row.PARTY,
                DESCRIPTIONAR: row.PARTY_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getOccupations: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/occupations?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.OCCUPATIONID,
                DESCRIPTION: row.OCCUPATIONNAME,
                DESCRIPTIONAR: row.OCCUPATIONNAME_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getMaritalStatusLookup: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/marital-status?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.MARSTATID,
                DESCRIPTION: row.MARSTAT,
                DESCRIPTIONAR: row.MARSTAT_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getFamilySize: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          // { url: `lookup/fam-size?status=${status}` },
          { url: `lookup/fam-size` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        const rows = response?.payload?.rows || [];

        if (params?.apiVersion === '2') {
          // if (!shouldTransformResponse()) {
          // return { data: response };
          // }
          return {
            data: {
              ...response,
              payload: {
                ...response.payload,
                rows: rows.map(row => ({
                  ID: row.FAMILYSIZEID,
                  DESCRIPTION: row.FAMILYSIZE,
                  DESCRIPTIONAR: row.FAMILYSIZE_AR,
                  ...row
                }))
              }
            }
          };
        }
        else if (params?.apiVersion === '1') {
          return {
            data: {
              ...response,
              payload: {
                ...response.payload,
                rows: rows.map(row => ({
                  ID: row.FAMILYSIZEID,
                  DESCRIPTION: row.FAMILYSIZE,
                  DESCRIPTIONAR: row.FAMILYSIZE_AR,
                  ...row
                }))
              }
            }
          };
        }
      },
    }),
    getTitles: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/title?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params.apiVersion == '2') {  //if we have explicit version param to be 2, then only check upgrade flag
          if (!shouldTransformResponse()) {
            return { data: response }; // Return as-is for v2 (upgraded)
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.TITLEID,
                DESCRIPTION: row.TITLE,
                DESCRIPTIONAR: row.TITLE_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getRegions: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Determine status parameter based on version
        const status = params.status || (apiVersion === '1' ? 'A' : 'ACTIVE');

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: `lookup/regions?status=${status}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params.apiVersion == '2') {  //if we have explicit version param to be 2, then only check upgrade flag
          if (!shouldTransformResponse()) {
            return { data: response }; // Return as-is for v2 (upgraded)
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.REGIONID,
                DESCRIPTION: row.REGION,
                DESCRIPTIONAR: row.REGION_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getCities: builder.query({
      queryFn: async (params = {}, api, extraOptions) => {
        // Determine API version from params or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (params && params.apiVersion) {
          apiVersion = params.apiVersion;
        } else {
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        const result = await versionedBaseQuery(
          { url: apiVersion === '1' ? `lookup/cities?districtid=${params?.REGIONREFERENCE || params?.regionId}&status=${params?.status || 'A'}` : `lookup/cities?REGIONREFERENCE=${params?.REGIONREFERENCE || params?.regionId}` },
          api,
          extraOptions
        );

        if (result.error) return result;

        // Transform response if needed
        const response = result.data;
        if (params?.apiVersion === '2') {
          if (!shouldTransformResponse()) {
            return { data: response };
          }
        }

        const rows = response?.payload?.rows || [];
        return {
          data: {
            ...response,
            payload: {
              ...response.payload,
              rows: rows.map(row => ({
                ID: row.CITYID,
                DESCRIPTION: row.CITY,
                DESCRIPTIONAR: row.CITY_AR,
                ...row
              }))
            }
          }
        };
      },
    }),
    getLoanReasons: builder.query(
      {
        queryFn: async (params = {}, api, extraOptions) => {
          // Determine API version from params or upgrade flag
          let apiVersion = '2'; // Default to V2

          if (params && params?.apiVersion) {
            apiVersion = params?.apiVersion;
          } else {
            apiVersion = getUpgradeFlag() ? '2' : '1';
          }

          // Choose base URL based on version
          const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

          // Create a simple fetchBaseQuery with the correct baseUrl
          const versionedBaseQuery = fetchBaseQuery({
            baseUrl: baseUrl,
            prepareHeaders: (headers) => {
              const token = getAuthToken();
              if (token) {
                headers.set('authorization', `Bearer ${token}`);
              }
              return headers;
            },
          });

          const result = await versionedBaseQuery(
            { url: `lookup/loan-reasons` },
            api,
            extraOptions
          );

          if (result.error) return result;

          // Transform response if needed
          const response = result.data;
          if (params?.apiVersion === '2') {
            if (!shouldTransformResponse()) {
              return { data: response };
            }
          }

          const rows = response?.payload?.rows || [];
          return {
            data: {
              ...response,
              payload: {
                ...response.payload,
                rows: rows.map(row => ({
                  ID: row.REASONID,
                  DESCRIPTION: row.REASON,
                  DESCRIPTIONAR: row.REASON_AR,
                  ...row
                }))
              }
            }
          };
        },
      }
    ),
    // Auto Lease V2 Lookup Endpoints
    getManufacturers: builder.query({
      query: (params) => ({
        url: `lookup/manufacturer`,
        params: { status: params?.status || 'ACTIVE' },
      }),
      transformResponse: (response) => response?.payload?.rows || [],
    }),
    getDealersLookup: builder.query({
      query: (params) => ({
        url: `lookup/dealers`,
        params: { status: params?.status || 'ACTIVE' },
      }),
      transformResponse: (response) => response?.payload?.rows || [],
    }),
    getColorsLookup: builder.query({
      query: () => ({
        url: `lookup/colors`,
      }),
      transformResponse: (response) => response?.payload?.rows || [],
    }),
    getAssetModels: builder.query({
      query: (params) => ({
        url: `lookup/models-by-manufacturer-v2`,
        params: { assetMakeId: params?.assetMakeId },
      }),
      transformResponse: (response) => response?.payload?.rows || [],
    }),
    getAssetModelTypes: builder.query({
      query: (params) => ({
        url: `lookup/model-types-by-model-v2`,
        params: { assetMakeId: params?.assetMakeId, assetModelId: params?.assetModelId },
      }),
      transformResponse: (response) => response?.payload?.rows || [],
    }),
    getAssetConditions: builder.query({
      query: () => ({
        url: `lookup/asset-condition`,
      }),
      transformResponse: (response) => response?.payload?.rows || [],
    }),
    getDocument: builder.query({
      queryFn: async (data, api, extraOptions) => {
        // Determine API version from data or upgrade flag
        let apiVersion = '2'; // Default to V2

        if (data && data.apiVersion) {
          // Explicit version provided in request
          apiVersion = data.apiVersion;
        } else {
          // Check upgrade flag to determine version
          apiVersion = getUpgradeFlag() ? '2' : '1';
        }

        // Choose base URL based on version
        const baseUrl = apiVersion === '2' ? env.API_ENDPOINT_V2 : env.API_ENDPOINT;

        // Create a simple fetchBaseQuery with the correct baseUrl
        const versionedBaseQuery = fetchBaseQuery({
          baseUrl: baseUrl,
          prepareHeaders: (headers) => {
            // Get token from localStorage and add to headers
            const token = getAuthToken();
            if (token) {
              headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
          },
        });

        // Make the request with just the endpoint path
        return versionedBaseQuery(
          { url: `app/document?id=${data.id}&docId=${data.doc_id}&applId=${data.applId}` },
          api,
          extraOptions
        );
      },
      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true
    }),
    initiateMobileChange: builder.mutation({
      query: (body) => {
        const deviceId = localStorage.getItem("@@TAMWEEL_USER_DEVICE_ID"); //Get deviceId from localStorage
        console.log("deviceId", deviceId);
        return {
          url: "auth/initiate-mobile-change",
          method: "POST",
          body: {
            iqamaId: body.iqamaId,
            oldMobileNumber: `966${body.oldMobile}`,
            newMobileNumber: `966${body.newMobile}`,
            deviceId,
          },
        };
      },
    }),
    verifyMobileChange: builder.mutation({
      query: (body) => {
        const deviceId = localStorage.getItem("@@TAMWEEL_USER_DEVICE_ID");
        return {
          url: "auth/verify-mobile-change",
          method: "POST",
          body: {
            iqamaId: body.iqamaId,
            oldMobileNumber: `966${body.oldMobile}`,
            newMobileNumber: `966${body.newMobile}`,
            deviceId,
            otp: hashedPassword(body.otp),
          },
        };
      },
    }),
    updatePrivacyConsent: builder.mutation({
      query: (body) => ({
        url: "app/update-privacy-consent",
        method: "POST",
        body,
      }),
    }),
    // ─── Salary / eligibility (v1-only — see the forced-v1 branch above) ────
    // Gate shown when opening a product application form: the salary the user
    // enters is checked against the product's minimum. The response carries
    // `success` for the requested product plus a `recommended_eligibility` map
    // of the other products they would qualify for.
    checkEligibility: builder.mutation({
      query: (body) => ({
        url: "app/check-eligibility",
        method: "POST",
        body,
      }),
    }),
    getSalary: builder.query({
      query: (body) => ({
        url: "app/get-salary",
        method: "POST",
        body,
      }),
      keepUnusedDataFor: 0,
    }),
    updateSalary: builder.mutation({
      query: (body) => ({
        url: "app/update-salary",
        method: "POST",
        body,
      }),
    }),
    // ─── Dynamic vendor catalog (v2-only) ──────────────────────────────────
    // Backed by GET /v2/vendors and GET /v2/vendors/:id. Pages that consume:
    //   FeaturedProductsV2 (Home), SchoolListingV2, DentalListingV2,
    //   SchoolProductsV2, DentalProductsV2.
    // Server-side filter args (all optional, AND-combined):
    //   subproductName: "EDUCATIONFEES" | "HEALTHCARE" | "OTHER" | …
    //   productCode:    "SMB.TA.PERSONAL.FINANCE.EDU" | "SMB.TA.MURABAHA.FINANCE"
    //   search:         vendor name substring
    //   page/limit:     pagination (backend caps limit at 20)
    getVendors: builder.query({
      query: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.page) qs.set("page", params.page);
        if (params.limit) qs.set("limit", params.limit);
        if (params.subproductName) qs.set("subproductName", params.subproductName);
        if (params.productCode) qs.set("productCode", params.productCode);
        if (params.search) qs.set("search", params.search);
        const q = qs.toString();
        return { url: q ? `vendors?${q}` : `vendors` };
      },
      transformResponse: (response) => ({
        data: response?.data || [],
        pagination: response?.pagination || null,
      }),
      transformErrorResponse: () => ({ error: "FAILED_TO_LOAD_VENDORS" }),
    }),
    // Fetched by SchoolProductsV2 / DentalProductsV2. `id` is the numeric
    // vendor.ID — VENDOR_ID (the "VND-001" string) is reserved for the
    // dealerId payload field on application create.
    getVendorById: builder.query({
      query: (id) => ({ url: `vendors/${id}` }),
      transformResponse: (response) => response?.data || null,
      transformErrorResponse: () => ({ error: "VENDOR_NOT_FOUND" }),
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useCheckEligibilityMutation,
  useGetSalaryQuery,
  useLazyGetSalaryQuery,
  useUpdateSalaryMutation,
  useSignInMutation,
  useVerifyMutation,
  useForgotPasswordMutation,
  useForgotPasswordVerificationMutation,
  useResetPasswordMutation,
  useValidateIdMutation,
  useRegisterMutation,
  useGetStatusMutation,
  useValidatePhoneMutation,
  useLogoutMutation,
  useGetPersonalInformationMutation,
  useCreateNewAppMutation,
  useCreateNewTawaruqAppMutation,
  useCreateNewMurabahaAppMutation,
  useCreateNewIjarahAppMutation,
  useInsertAppRestructureMutation,
  useInsertAppTopupMutation,
  useInsertAppBuyoutMutation,
  useInsertAppSettlementMutation,
  useInsertAppAdhocChargeMutation,
  useInsertAppPartialSettlementMutation,
  useGetSettlementQuoteMutation,
  useGetNocLetterMutation,
  useGetUserAppsQuery,
  useGetApplicationQuery,
  useUpdateAppDetailsMutation,
  useUpdateAppDetailsV2Mutation,
  useUpdateCustomerDetailsMutation,
  useGetApplicationOfferQuery,
  useUpdateAppOfferMutation,
  useCheckPasswordMutation,
  useGetMaritalStatusMutation,
  useUpdateMaritalStatusMutation,
  useGetEligibilityMutation,
  useUpdateEligibilityMutation,
  useGetAddressDetailMutation,
  useUpdateAddressDetailMutation,
  useGetpersonalInfoMutation,
  useUpdateUserEmailMutation,
  useGetDocumentsQuery,
  useGetPreAppStatusQuery,
  useIbanVerificationMutation,
  useUpdateGeneralInfoMutation,
  useDeleteApplMutation,
  useGetManageOffersMutation,
  useUpdateManageOffersMutation,
  useGetInstlmntDashboardMutation,
  useInitiateMobileChangeMutation,
  useVerifyMobileChangeMutation,
  useUpdatePrivacyConsentMutation,
  // Lookup hooks
  useGetNationalitiesQuery,
  useGetEmploymentSectorsQuery,
  useGetEducationLevelsQuery,
  useGetResidentialStatusQuery,
  useGetEmploymentCategoriesQuery,
  useGetRelatedPartyQuery,
  useGetOccupationsQuery,
  useGetMaritalStatusLookupQuery,
  useGetFamilySizeQuery,
  useGetTitlesQuery,
  useGetRegionsQuery,
  useGetCitiesQuery,
  useGetLoanReasonsQuery,
  useLazyGetDocumentQuery,
  // Auto Lease V2 lookup hooks
  useGetManufacturersQuery,
  useGetDealersLookupQuery,
  useGetColorsLookupQuery,
  useGetAssetModelsQuery,
  useLazyGetAssetModelsQuery,
  useGetAssetModelTypesQuery,
  useLazyGetAssetModelTypesQuery,
  useGetAssetConditionsQuery,
  // Dynamic vendor catalog (V2)
  useGetVendorsQuery,
  useLazyGetVendorsQuery,
  useGetVendorByIdQuery,
  useLazyGetVendorByIdQuery,
} = api;

